import React, { useRef, type FunctionComponent, useState, useEffect } from 'react'
import { Text, Tooltip, useBooleanState } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { type EnterpriseAccountDatum } from 'domains/account-management/components/accounts-list/utils/AccountManagementListAccounts.types'
import { RoleManagementRoleOverviewModal } from 'domains/role-management'

export interface ListRolesCellProps {
  roles: EnterpriseAccountDatum['roles']
}

export const ListRolesCell: FunctionComponent<ListRolesCellProps> = ({ roles }) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const [overflowingChildren, setOverflowingChildren] = useState<number>(0)

  useEffect(() => {
    // if the parent element is overflowing, count the number of children that are overflowing.
    // this is so we can count how many "+x" roles are hidden.
    // all this is only necessary because our Grid component doesn't support variable row heights.
    // once we have that, we can drop this code and let the row expand to fix all the Role content.
    if (parentRef.current) {
      const parentRect = parentRef.current.getBoundingClientRect()
      const children = Array.from(parentRef.current.children)
      let overflowCount = 0

      children.forEach(child => {
        const childRect = child.getBoundingClientRect()
        if (childRect.bottom > parentRect.bottom) {
          overflowCount++
        }
      })

      if (overflowCount > overflowingChildren) {
        // +1 to account for the <Text>+x</Text> child not yet rendered.
        // not the most optimal solution, but it works.
        setOverflowingChildren(overflowCount + 1)
      }
    }
  }, [overflowingChildren])

  if (!roles) return <span>-</span>

  const maxRolesCount = roles.length - overflowingChildren
  const areRolesHidden = roles.length > maxRolesCount
  const hiddenRoles = roles.slice(maxRolesCount).map(role => role.name)
  const hiddenRoleCount = roles.length - maxRolesCount
  const hiddenRoleToolTipMessage = hiddenRoles.join(', ')

  return (
    <ClickableRolesContainer ref={parentRef}>
      {roles.slice(0, maxRolesCount).map(role => (
        <ClickableRole key={role.name} role={role} />
      ))}
      {areRolesHidden && (
        <Tooltip target={<Text size="medium">+{hiddenRoleCount}</Text>}>
          {hiddenRoleToolTipMessage}
        </Tooltip>
      )}
    </ClickableRolesContainer>
  )
}

const ClickableRole = ({ role }: { role: EnterpriseAccountDatum['roles'][0] }) => {
  const [isShowing, show, hide] = useBooleanState(false)

  return (
    <>
      <Text variant="link" size="medium" onClick={show}>
        {role.name}
      </Text>
      {isShowing && (
        <RoleManagementRoleOverviewModal roleId={role.id} modalProps={{ onRequestClose: hide }} />
      )}
    </>
  )
}

const ClickableRolesContainer = styled.div({
  display: 'flex',
  gap: 5,
  alignItems: 'center',
  flexWrap: 'wrap',
  height: 55,
  overflow: 'hidden',
})
